import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import OfficeBuilding from '@atlaskit/icon/glyph/office-building';
import { trackLinkClick } from '@atlassian/temp-analytics';
import { CustomerAddressQueryItem } from '../../../customer-address';
import './widget-items.scss';

export type CustomerAddressWidgetItemType = CustomerAddressQueryItem & {
  url: string;
  heading?: string;
  version?: number;
};

/**
 * Component for Customer Address Widget on the Home page
 * @constructor
 */
const CustomerAddressWidgetItemComponent = ({
  name,
  url,
  timestamp,
  addressLine1,
  addressLine2,
  city,
  state,
  country,
  postcode,
  heading = '',
  version,
}: CustomerAddressWidgetItemType) => {
  const address = useMemo(() => {
    return [addressLine1, addressLine2, city, state, country, postcode].reduce((acc, content, idx, allContent) => {
      const comma = idx !== allContent.length - 1 ? ', ' : '';
      acc += content ? content + comma : '';
      return acc;
    }, '');
  }, [addressLine1, addressLine2, city, state, country, postcode]);
  const isVersion2 = version === 2;
  const renderSubTitle = () => {
    return <div className="sub-title line-ellipsis">{address ? address : 'All sold-to addresses'}</div>;
  };
  const renderTitle = () => {
    return <div className="title">{name}</div>;
  };

  return (
    <div className="flex justify-space-between mb-12 pv-12 customer-address-widget-item">
      <div className="left-side" title={address ? address : ''}>
        <div className="content">
          <Link
            to={url}
            onClick={() => {
              trackLinkClick('widgetItemClick', { source: heading });
            }}
          >
            <div className="flex">
              {isVersion2 ? (
                <>
                  <span className="widget-item-icon">
                    <OfficeBuilding label="Customer icon" size="small" />
                  </span>
                  <div className="align-self-center">
                    {renderTitle()}
                    {renderSubTitle()}
                  </div>
                </>
              ) : (
                renderTitle()
              )}
            </div>
          </Link>
        </div>
        {isVersion2 || <div className="content">{renderSubTitle()}</div>}
      </div>
      <div className="right-side">
        <div className="content">
          <div className="timestamp">{formatDistanceToNowStrict(new Date(timestamp), { addSuffix: true })}</div>
        </div>
      </div>
    </div>
  );
};

export const CustomerAddressWidgetItem = React.memo(CustomerAddressWidgetItemComponent);
